import { types, Instance } from 'mobx-state-tree';
import { SegmentInfoModel } from './WorkoutModel';
import { YOGA_TEMPLATE_TYPES } from '@/constants/workoutTemplateTypes';
import { nowMs } from '@/services/datetime';

// ─── SegmentChapterInfoModel ────────────────────────────────────
export const SegmentChapterInfoModel = types
  .model('SegmentChapterInfoModel', {
    id: types.number,
    unique_name: types.string,
    title: types.string,
    description: types.maybeNull(types.string),
    start_time_ms: types.number,
    end_time_ms: types.number,
  })
  .views((self) => ({
    get durationMs(): number {
      return self.end_time_ms - self.start_time_ms;
    },
  }));

export type ISegmentChapterInfoModel = Instance<typeof SegmentChapterInfoModel>;

// ─── UserSegmentChapterModel ────────────────────────────────────
export const UserSegmentChapterModel = types
  .model('UserSegmentChapterModel', {
    id: types.number,
    total_progress_time_ms: types.maybeNull(types.number),
    segment_chapter_info: SegmentChapterInfoModel,
  })
  .views((self) => ({
    get durationMs(): number {
      return self.segment_chapter_info.durationMs;
    },
    get title(): string {
      return self.segment_chapter_info.title;
    },
    get startTimeMs(): number {
      return self.segment_chapter_info.start_time_ms;
    },
    get endTimeMs(): number {
      return self.segment_chapter_info.end_time_ms;
    },
  }))
  .actions((self) => ({
    setProgressMs(ms: number) {
      self.total_progress_time_ms = ms;
    },
  }));

export type IUserSegmentChapterModel = Instance<typeof UserSegmentChapterModel>;

// ─── GuidedWorkoutSegmentModel ──────────────────────────────────
export const GuidedWorkoutSegmentModel = types
  .model('GuidedWorkoutSegmentModel', {
    id: types.number,
    group: types.string,
    segment_type: types.string,
    segment_info: SegmentInfoModel,
    completion_time_ms: types.number,
    chapters: types.array(UserSegmentChapterModel),
    started_at: types.maybeNull(types.number),
    completed_at: types.maybeNull(types.number),
    current_user_chapter_id: types.maybeNull(types.number),
  })
  .views((self) => ({
    get totalDurationMs(): number {
      if (self.chapters.length === 0) return 0;
      return self.chapters[self.chapters.length - 1].endTimeMs;
    },

    get videoUrl(): string | null {
      if (self.segment_info.videoUrls.length === 0) return null;
      return self.segment_info.videoUrls[0];
    },

    get videoHash(): string | null {
      if (self.segment_info.videoHashes.length === 0) return null;
      return self.segment_info.videoHashes[0];
    },

    get videoSize(): number | null {
      if (self.segment_info.videoSizes.length === 0) return null;
      return self.segment_info.videoSizes[0];
    },

    chapterAtIndex(index: number): IUserSegmentChapterModel | null {
      if (index < 0 || index >= self.chapters.length) return null;
      return self.chapters[index];
    },

    findChapterIndexAtTime(timeMs: number): number {
      for (let i = self.chapters.length - 1; i >= 0; i--) {
        if (timeMs >= self.chapters[i].startTimeMs) {
          return i;
        }
      }
      return 0;
    },

    get chapterMarkers(): { position: number; title: string }[] {
      const total = this.totalDurationMs;
      if (total <= 0) return [];
      return self.chapters
        .filter((_, idx) => idx > 0) // skip first chapter (starts at 0)
        .map((ch) => ({
          position: ch.startTimeMs / total,
          title: ch.title,
        }));
    },
  }))
  .actions((self) => ({
    markStarted() {
      if (!self.started_at) {
        self.started_at = nowMs();
      }
    },
    markCompleted() {
      self.completed_at = nowMs();
    },
  }));

export type IGuidedWorkoutSegmentModel = Instance<
  typeof GuidedWorkoutSegmentModel
>;

// ─── GuidedWorkoutModel ─────────────────────────────────────────
const GuidedWorkoutModel = types
  .model('GuidedWorkoutModel', {
    id: types.number,
    workout_template_type: types.string,
    segment: GuidedWorkoutSegmentModel,
    started_at: types.maybeNull(types.number),
    completed_at: types.maybeNull(types.number),
  })
  .views((self) => ({
    get chapters(): IUserSegmentChapterModel[] {
      return self.segment.chapters as IUserSegmentChapterModel[];
    },
    get isYoga(): boolean {
      return YOGA_TEMPLATE_TYPES.includes(self.workout_template_type);
    },
    get exerciseCount(): number {
      return Math.max(self.segment.chapters.length - 1, 1);
    },
    get exerciseCountLabel(): string {
      return YOGA_TEMPLATE_TYPES.includes(self.workout_template_type)
        ? 'Poses'
        : 'Exercises';
    },
    get totalDurationMs(): number {
      return self.segment.totalDurationMs;
    },
    get videoUrl(): string | null {
      return self.segment.videoUrl;
    },
    get videoHash(): string | null {
      return self.segment.videoHash;
    },
    get videoSize(): number | null {
      return self.segment.videoSize;
    },
    get equipment(): string[] {
      return self.segment.segment_info.equipment as string[];
    },
  }));

export type IGuidedWorkoutModel = Instance<typeof GuidedWorkoutModel>;

export default GuidedWorkoutModel;
